import React, { useEffect, useMemo, useRef, useState } from "react";
import { Globe, Search, X } from "lucide-react";
import { LANGUAGES, getLanguage } from "./languageMap";
import { useTranslation } from "./useTranslation";

/**
 * LanguagePicker - floating button (bottom-left) that opens a heritage-
 * styled drawer listing every supported language, grouped by region with
 * a search box at the top.
 *
 * Stays out of the way on the right side (floating WhatsApp lives there).
 */
export default function LanguagePicker() {
  const { current, changeLanguage } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const drawerRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);

  const lang = getLanguage(current);

  // Group languages by their `group` field, preserving order from LANGUAGES
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? LANGUAGES.filter(
          (l) =>
            l.name.toLowerCase().includes(q) ||
            l.native.toLowerCase().includes(q) ||
            l.code.toLowerCase().includes(q)
        )
      : LANGUAGES;
    const map = new Map();
    filtered.forEach((l) => {
      if (!map.has(l.group)) map.set(l.group, []);
      map.get(l.group).push(l);
    });
    return Array.from(map.entries());
  }, [query]);

  // Close on Escape + click-outside
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e) => {
      if (
        drawerRef.current &&
        !drawerRef.current.contains(e.target) &&
        !triggerRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    // Auto-focus the search field when the drawer opens
    const focusId = window.setTimeout(() => searchRef.current?.focus(), 80);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
      window.clearTimeout(focusId);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="lang-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Current language ${lang.name}. Open language picker`}
        onClick={() => setOpen((v) => !v)}
      >
        <Globe size={16} strokeWidth={1.6} />
        <span className="lang-trigger-code">{current.toUpperCase()}</span>
        <span className="lang-trigger-native">{lang.native}</span>
      </button>

      {open && (
        <aside
          ref={drawerRef}
          className="lang-drawer"
          role="dialog"
          aria-label="Choose a language"
        >
          <div className="lang-drawer-head">
            <div className="lang-drawer-title">
              <span className="folio">FOLIO · LANGUAGES</span>
              <h3 className="display-italic">Read in your tongue.</h3>
            </div>
            <button
              type="button"
              className="lang-drawer-close"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              <X size={14} strokeWidth={1.6} />
            </button>
          </div>

          <label className="lang-search">
            <Search size={14} strokeWidth={1.6} />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search 56 languages…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>

          <div className="lang-list">
            {grouped.length === 0 ? (
              <p className="lang-empty mono">No matches</p>
            ) : (
              grouped.map(([group, items]) => (
                <div key={group} className="lang-group">
                  <div className="lang-group-label mono">{group}</div>
                  <ul>
                    {items.map((l) => {
                      const active = l.code === current;
                      return (
                        <li key={l.code}>
                          <button
                            type="button"
                            className={`lang-item ${active ? "is-active" : ""}`}
                            onClick={() => {
                              setOpen(false);
                              changeLanguage(l.code);
                            }}
                          >
                            <span className="lang-item-native">{l.native}</span>
                            <span className="lang-item-name">{l.name}</span>
                            <span className="lang-item-code mono">
                              {l.code.toUpperCase()}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))
            )}
          </div>

          <div className="lang-foot mono">
            Translation by Google · automatic, not perfect
          </div>
        </aside>
      )}
    </>
  );
}
